import React, { useCallback, useMemo } from "react"
import {
    APIGroundTruthModel,
    getGroundTruthID
} from "../../../../../../../../Models/API/APIGroundTruthModel"
import { useDispatch } from "react-redux"
import { Text } from "dash-styled"
import { AppDetailBase } from "../../../AppDetailBase/AppDetailBase"
import { push } from "connected-react-router"
import { routePaths } from "../../../../../../../../Routes"
import { fromNow } from "../../../../../../../../Helpers/Functions/dateFormat"
import { useGroundTruthList } from "../../../../../../../../Hooks/useGroundTruthList"
import {
    hasValue,
    FetchState,
    getValue
} from "../../../../../../../../Models/Client/FetchState"
import { loadMoreGroundTruthList } from "../../../../../../../../Actions/API/AppInstance/AppInstanceBaselineDataActions"
import { Table, TableColumn } from "resha"
import { TableHeader } from "../../../../../../../Common/Table/TableHeader"
import { FilterAndSortExpression } from "../../../../../../../../Models/Client/FilterAndSortExpression"
import { SortExpression } from "../../../../../../../../Models/Client/SortExpression"
import { BaselineDataDetail } from "./BaselineDataDetail"
import { BaselineBanner } from "./BaselineBanner"
import { NothingMatched } from "../../../../../../../Common/Empty/NothingMatched"
import { useSelection } from "../../../../../../../../Hooks/useSelection"
import { FlatButton } from "../../../../../../../Common/FlatButton/FlatButton"
import { downloadAsJSON } from "../../../../../../../../Helpers/Functions/downloadAs"
import { MainThunkDispatch } from "../../../../../../../../Reducers"
import { groundTruthSortOptions } from "../../../Common/constants"
import { Redirect } from "react-router"
import {
    FilterExpression
    /*ConstFilterOperator*/
} from "../../../../../../../../Models/Client/FilterExpression"
import { useGroundTruthTotalCount } from "../../../../../../../../Hooks/useGroundTruthTotalCount"
import { useAppInstance } from "../../../../../../../../Hooks/useAppInstance"
import { isAppInstanceEnabled } from "../../../../../../../../Models/API/AppInstanceModel"
// import { isQualityValue } from "../../../../../../../../Models/DataProgram/Measurement"
// import { QualityColumnHeader } from "./QualityColumnHeader"
import { useMetricSelection } from "../../../../../../../../Hooks/useMetricSelection"
import { hasAppPermission } from "../../../../../../../../Models/Client/ProjectRole"
import { useAssumedRoles } from "../../../../../../../../Hooks/useAssumedRoles"
import { useAppState } from "../../../../../../../../Hooks/useReduxState"
import { selectHasFreemiumExpired } from "../../../../../../../../Helpers/Functions/Selectors/auth"
import { useBoolean } from "../../../../../../../../Hooks/useBoolean"

interface BaselineDataProps {
    orgUsername: string
    appID: string
    /**
     * ID of the ground truth to show detail modal.
     * `undefined` means no need to show modal.
     * `null` indicates that the requester wants to show the first item of the list once the list is fetched.
     */
    groundTruthID?: string | null
    filterAndSort: FilterAndSortExpression<APIGroundTruthModel>
}

const getColumns = (
    qualityName: string | undefined,
    qualityNames: { [name: string]: string },
    onChangeQualityName: (qualityName: string) => void
): TableColumn<APIGroundTruthModel>[] => [
    {
        renderHeader: () => "Data point",
        render: model => {
            return (
                <Text className="m-0" weight="semibold">
                    {getGroundTruthID(model)}
                </Text>
            )
        },
        className: "text-smoke-610"
    },
    {
        renderHeader: () => "Measurements",
        render: (model: APIGroundTruthModel) => model.used,
        className: "text-grape-800"
    },
    // TODO: restore when endpoint is ready
    /*
    {
        renderHeader: () => (
            <QualityColumnHeader
                qualityNames={qualityNames}
                qualityName={qualityName}
                onChangeQualityName={onChangeQualityName}
            />
        ),
        render: (model: APIGroundTruthModel) => {
            if (
                qualityName === undefined ||
                model.baselineQuality === null ||
                model.baselineQuality === undefined
            ) {
                return ""
            }

            const quality = model.baselineQuality[qualityName]

            if (isQualityValue(quality)) {
                return quality.value.toPrecision(2)
            }

            return "-"
        },
        headerClassName: "w-1/6",
        className: "w-1/6 text-grape-800"
    },
    */
    {
        renderHeader: () => "Source",
        render: (model: APIGroundTruthModel) =>
            model.type === "OWNER" ? "Uploaded" : "Processed with super.AI",
        className: "text-grape-800"
    },
    {
        renderHeader: () => "Created",
        render: (model: APIGroundTruthModel) => fromNow(model.created),
        className: "text-grape-800"
    }
]

interface AppCustomizeBaselineDataIsolatedProps {
    orgUsername: string
    appID: string
    selection: APIGroundTruthModel[]
    groundTruthID?: string | null
    groundTruthListFetch: FetchState<{
        items: APIGroundTruthModel[]
        total: number
    }>
    totalCountFetch: FetchState<number>
    filterAndSort: FilterAndSortExpression<APIGroundTruthModel>
    qualityName: string | undefined
    qualityNames: { [name: string]: string }
    onClickRow: (item: APIGroundTruthModel) => void
    onChangeSort: (sort: SortExpression<APIGroundTruthModel>) => void
    onChangeFilter: (filter: FilterExpression<APIGroundTruthModel>[]) => void
    onAdd: () => void
    onLoadMore: () => Promise<void>
    onToggleSelection: (item: APIGroundTruthModel) => void
    onDownloadSelection: () => void
    onChangeQualityName: (qualityName: string) => void
}

const AppCustomizeBaselineDataIsolated = ({
    orgUsername,
    appID,
    groundTruthID,
    groundTruthListFetch,
    filterAndSort,
    totalCountFetch,
    qualityName,
    qualityNames,
    onChangeSort,
    onChangeFilter,
    onClickRow,
    onAdd,
    onLoadMore,
    selection,
    onToggleSelection,
    onDownloadSelection,
    onChangeQualityName
}: AppCustomizeBaselineDataIsolatedProps) => {
    const instanceFetch = useAppInstance(appID)
    const items = hasValue(groundTruthListFetch)
        ? groundTruthListFetch.value.items
        : undefined
    const filteredCount = hasValue(groundTruthListFetch)
        ? groundTruthListFetch.value.total
        : 0
    const renderEmpty = () => <NothingMatched messageVariant="try" />
    const columns = useMemo(
        () => getColumns(qualityName, qualityNames, onChangeQualityName),
        [qualityName, qualityNames, onChangeQualityName]
    )

    const [userExpiryContact, showUserExpiryContact, hideUserExpiryContact] =
        useBoolean(false)
    const hasFreemiumPlanExpired = useAppState(selectHasFreemiumExpired)

    const handleAdd = useCallback(
        () => (hasFreemiumPlanExpired ? showUserExpiryContact() : onAdd()),
        [hasFreemiumPlanExpired, onAdd, showUserExpiryContact]
    )

    const isAppEnabled = isAppInstanceEnabled(getValue(instanceFetch))
    const rolesFetch = useAssumedRoles(appID)

    const render = () => {
        return (
            <>
                {isAppEnabled &&
                    hasAppPermission(
                        getValue(rolesFetch) ?? [],
                        "ground-truth:create"
                    ) && <BaselineBanner onAdd={onAdd} />}
                <TableHeader
                    totalCount={getValue(totalCountFetch)}
                    sortOptions={{
                        fields: groundTruthSortOptions,
                        applied: filterAndSort.sort,
                        onChange: onChangeSort
                    }}
                    extraActions={
                        selection.length > 0 ? (
                            <FlatButton
                                onClick={onDownloadSelection}
                                className="ml-2"
                            >
                                Download selected
                            </FlatButton>
                        ) : null
                    }
                />
                <Table
                    columns={columns}
                    getKey={getGroundTruthID}
                    checkedItemIds={selection.map(getGroundTruthID)}
                    itemCount={filteredCount}
                    items={items}
                    className="w-full h-0 min-h-0 mt-0"
                    loadMore={onLoadMore}
                    onRowClick={onClickRow}
                    renderEmpty={renderEmpty}
                    onAdd={isAppEnabled ? handleAdd : undefined}
                    onToggleRowCheck={onToggleSelection}
                />
            </>
        )
    }
    const renderModal =
        groundTruthID === undefined
            ? undefined
            : () => (
                  <BaselineDataDetail
                      orgUsername={orgUsername}
                      appID={appID}
                      groundTruthID={
                          groundTruthID === null ? undefined : groundTruthID
                      }
                      filterAndSort={filterAndSort}
                  />
              )

    return (
        <AppDetailBase
            orgUsername={orgUsername}
            id={appID}
            breadcrumbs={[]}
            title="Ground-Truth Data"
            fixedContentWidth={false}
            render={render}
            renderModalView={renderModal}
            forceUserExpiryContact={userExpiryContact}
            resetUserExpiryContact={hideUserExpiryContact}
        />
    )
}

export const AppCustomizeBaselineData = ({
    orgUsername,
    appID,
    groundTruthID,
    filterAndSort
}: BaselineDataProps) => {
    const dispatch = useDispatch<MainThunkDispatch>()
    const { qualityName, setQualityName, qualityNames } =
        useMetricSelection(appID)
    const groundTruthListFetch = useGroundTruthList(appID, filterAndSort)
    const totalCountFetch = useGroundTruthTotalCount(appID)
    const [selection, toggleSelection, clearSelection] =
        useSelection<APIGroundTruthModel>(
            getGroundTruthID,
            hasValue(groundTruthListFetch)
                ? groundTruthListFetch.value.items
                : []
        )

    const handleAdd = useCallback(
        () =>
            dispatch(
                push(
                    routePaths.appCustomizeBaselineDataAddData(
                        orgUsername,
                        appID
                    )
                )
            ),
        [dispatch, appID, orgUsername]
    )
    const handleLoadMore = useCallback(
        () => dispatch(loadMoreGroundTruthList(appID)),
        [appID, dispatch]
    )
    const handleChangeSort = useCallback(
        (sort: SortExpression<APIGroundTruthModel>) => {
            clearSelection()
            dispatch(
                push(
                    routePaths.appCustomizeBaselineData(orgUsername, appID, {
                        ...filterAndSort,
                        sort
                    })
                )
            )
        },
        [clearSelection, dispatch, orgUsername, appID, filterAndSort]
    )
    const handleChangeFilter = useCallback(
        (filter: FilterExpression<APIGroundTruthModel>[]) => {
            clearSelection()
            dispatch(
                push(
                    routePaths.appCustomizeBaselineData(orgUsername, appID, {
                        ...filterAndSort,
                        filter
                    })
                )
            )
        },
        [clearSelection, dispatch, orgUsername, appID, filterAndSort]
    )
    const handleRowClick = useCallback(
        (model: APIGroundTruthModel) =>
            dispatch(
                push(
                    routePaths.appCustomizeBaselineDataDetail(
                        orgUsername,
                        appID,
                        getGroundTruthID(model),
                        filterAndSort
                    )
                )
            ),
        [dispatch, orgUsername, appID, filterAndSort]
    )
    const handleDownloadSelection = useCallback(
        () => downloadAsJSON(selection, `${appID}__baseline_data`),
        [selection, appID]
    )

    if (
        groundTruthID === null &&
        hasValue(groundTruthListFetch) &&
        groundTruthListFetch.value.items.length > 0
    ) {
        return (
            <Redirect
                to={routePaths.appCustomizeBaselineDataDetail(
                    orgUsername,
                    appID,
                    String(groundTruthListFetch.value.items[0].id),
                    filterAndSort
                )}
            />
        )
    }

    return (
        <AppCustomizeBaselineDataIsolated
            orgUsername={orgUsername}
            appID={appID}
            selection={selection}
            groundTruthID={groundTruthID}
            groundTruthListFetch={groundTruthListFetch}
            totalCountFetch={totalCountFetch}
            filterAndSort={filterAndSort}
            qualityName={qualityName}
            qualityNames={qualityNames}
            onChangeSort={handleChangeSort}
            onChangeFilter={handleChangeFilter}
            onClickRow={handleRowClick}
            onAdd={handleAdd}
            onLoadMore={handleLoadMore}
            onToggleSelection={toggleSelection}
            onDownloadSelection={handleDownloadSelection}
            onChangeQualityName={setQualityName}
        />
    )
}
